import React, { useCallback, useEffect, useState } from 'react';
import sortBy from 'lodash/sortBy';
import { useForm } from 'react-hook-form';

import { Box } from '@nerdwallet/currency/components/Box';
import { RadioGroup } from '@nerdwallet/currency/components/RadioGroup';
import { SecureEncryptionMessage } from '@nerdwallet/currency/components/SecureEncryptionMessage';
import { Type } from '@nerdwallet/currency/components/Type';

import { useLazyQuery, useMutation } from '@apollo/client';
import { SegmentPageView } from '@lib/segment';
import {
  AuthenticateCreditProfileResponseUnion,
  TuCreditReportIdentityProfile,
} from '@generated-gql/generated-types';
import {
  TuStep,
  useTuStepper,
} from '@components/transunion-flow/tu-modal-stepper';
import { useTuVisibilityContext } from '@components/transunion-flow/tu-visibility-context';
import { isTUBlocked } from '@components/transunion-flow/utils';
import ContinueButton from '../components/continue-button';
import FriendlyTitle from '../components/friendly-title';
import {
  AUTHENTICATE_CREDIT_PROFILE_QUESTIONS_MUTATION,
  TU_AUTHENTICATION_STATUS_PROFILE_QUERY,
} from '../queries';

import styles from './verification-questions.module.scss';

interface Props {
  onActivationSuccess: (profile: TuCreditReportIdentityProfile) => void;
}

const VerificationQuestions = ({ onActivationSuccess }: Props) => {
  const { setTuVisibility } = useTuVisibilityContext();
  const { authStatusProfile, setAuthStatusProfile } = useTuStepper();
  const [isValid, setIsValid] = useState(false);
  const { setValue, watch } = useForm();

  const [getAuthenticationStatusProfile] = useLazyQuery(
    TU_AUTHENTICATION_STATUS_PROFILE_QUERY,
  );

  const handleSetTuAuthenticationSuccess = useCallback(
    async (data: AuthenticateCreditProfileResponseUnion) => {
      // The backend will return either a TuCreditReportIdentityProfile
      // if the authentication succeeded or CreditProfileAuthStatusProfile
      // it there are more questions to answer
      if (data) {
        if (data.__typename === 'TuCreditReportIdentityProfile') {
          onActivationSuccess(data);
        } else {
          // There may be more verification questions to answer
          setAuthStatusProfile(data);
        }
      }
    },
    [setAuthStatusProfile, onActivationSuccess],
  );

  const handleSetTuAuthenticationFail = useCallback(async () => {
    // Refetch the latest state of the Auth Status Profile to see if the user is blocked
    const { data } = await getAuthenticationStatusProfile({
      fetchPolicy: 'network-only',
    });
    if (data?.tuAuthenticationStatusProfile) {
      setAuthStatusProfile(data.tuAuthenticationStatusProfile);
      if (isTUBlocked(data.tuAuthenticationStatusProfile)) {
        setTuVisibility({
          show: true,
          tuStep: TuStep.TU_BLOCKED,
        });
      }
    }
  }, [getAuthenticationStatusProfile, setAuthStatusProfile, setTuVisibility]);

  const [submitAnswers, { loading }] = useMutation(
    AUTHENTICATE_CREDIT_PROFILE_QUESTIONS_MUTATION,
    {
      onCompleted: (data) => {
        handleSetTuAuthenticationSuccess(
          data.authenticateCreditProfileQuestions,
        );
      },
      onError: handleSetTuAuthenticationFail,
    },
  );

  const questions = authStatusProfile?.questions || [];
  const watchFields = watch();

  useEffect(() => {
    if (
      questions.length &&
      questions.length === Object.keys(watchFields)?.length
    ) {
      setIsValid(true);
    }
  }, [questions?.length, watchFields]);

  const onSubmitForm = useCallback(async () => {
    const answers = Object.keys(watchFields).map((questionId) => ({
      questionId,
      answerId: watchFields[questionId],
    }));
    await submitAnswers({ variables: { answers } });
  }, [submitAnswers, watchFields]);

  const handleOnChange = useCallback(
    ({ questionId, answerId }) => setValue(questionId, answerId),
    [setValue],
  );

  return (
    <SegmentPageView name="credit_score_verification_questions">
      <div className={styles.container}>
        <FriendlyTitle title="Finally, answer these questions to verify your ID." />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmitForm();
          }}
        >
          {sortBy(questions, 'sequence').map((question) => (
            <div key={question.id} className={styles.verificationQuestion}>
              <Type component="label" bold className={styles.questionLabel}>
                {question.text}
              </Type>

              <RadioGroup
                name={question.id}
                onChange={({ target: { value } }) => {
                  const answer = { questionId: question.id, answerId: value };
                  handleOnChange(answer);
                }}
                options={sortBy(question.answers, 'sequence').map((answer) => ({
                  value: answer.id,
                  label: answer.text,
                }))}
              />
            </div>
          ))}

          <Box marginTop={4} display="block">
            <ContinueButton
              isDisabled={!isValid || loading}
              isLoading={loading}
            />
          </Box>
          <Box marginTop={4}>
            <SecureEncryptionMessage />
          </Box>
        </form>
      </div>
    </SegmentPageView>
  );
};

export default VerificationQuestions;
